import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensHistoryPoints, fetchTokensHistoryRatios } from "@/services/queries/tokens/tokensScreenerQueries";
//

type RangeKey = '7d' | '30d' | '90d' | '365d';

const RANGE_TO_POINTS: Record<RangeKey, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '365d': 365,
};

const STABLE_REGEX = /(USDT|USDC|USD|DAI|FDUSD|TUSD)$/i;

export type Props = {
  rows: Array<Record<string, unknown>>;
  range: RangeKey;
  xKey: string;
  transform: 'raw' | 'momentum' | 'vol' | 'dominance';
  windowDays: 7 | 30 | 90;
  // Placeholder: if true, apply hierarchical clustering to reorder rows/columns (not implemented yet)
  clustered?: boolean;
  excludeStables: boolean;
  search: string;
  mcMin: number | '';
  mcMax: number | '';
  labelFromCatalog: (key: string) => string;
  // catalogMap not needed here; keep API minimal
};

const HeatmapChart: React.FC<Props> = ({ rows, range, xKey, transform, windowDays, clustered, excludeStables, search, mcMin, mcMax, labelFromCatalog }) => {
  const heatmapRows = useMemo(() => {
    const items: Array<{ ticker: string; mc: number }> = [];
    for (const r of rows as Array<Record<string, unknown>>) {
      const ticker = String(r["tokenSymbol"] ?? "");
      if (!ticker) continue;
      if (excludeStables && STABLE_REGEX.test(ticker)) continue;
      if (search) {
        const name = String(r["tokenName"] ?? r["name"] ?? "");
        const s = search.toLowerCase();
        if (!ticker.toLowerCase().includes(s) && !name.toLowerCase().includes(s)) continue;
      }
      const mcRaw = (r["market_cap"] as number | null) ?? (r["market_cap_usd"] as number | null) ?? null;
      const mc = mcRaw == null ? NaN : Number(mcRaw);
      if (!Number.isFinite(mc) || mc <= 0) continue;
      if (mcMin !== '' && mc < Number(mcMin)) continue;
      if (mcMax !== '' && mc > Number(mcMax)) continue;
      items.push({ ticker, mc });
    }
    const MAX_ROWS = 30;
    return items.sort((a, b) => b.mc - a.mc).slice(0, MAX_ROWS).reverse();
  }, [rows, excludeStables, search, mcMin, mcMax]);

  const tickers = useMemo<string[]>(() => heatmapRows.map((r) => r.ticker), [heatmapRows]);
  const tickersCsv = useMemo(() => tickers.join(','), [tickers]);
  const isRatioMetric = xKey.startsWith('ratio:');

  const historyQuery = useQuery({
    queryKey: ['tokens', 'history', isRatioMetric ? 'ratios' : 'points', 'heatmap', tickersCsv, xKey],
    queryFn: ({ signal }) => {
      if (isRatioMetric) {
        return fetchTokensHistoryRatios({ tickersCsv, ratioKeysCsv: xKey, signal });
      } else {
        return fetchTokensHistoryPoints({ tickersCsv, metricKeysCsv: xKey, signal });
      }
    },
    enabled: Boolean(tickersCsv && xKey),
    staleTime: 60_000,
  });

  const option = useMemo<EChartsOption>(() => {
    type Series = { dates: Array<string | number>; values: Array<number | null> };
    const byTicker = new Map<string, Series>();
    
    // Process the multi-ticker response
    const historyData = historyQuery.data || [];
    for (const tokenData of historyData) {
      const t = tokenData.ticker;
      const m = tokenData.metrics?.[0];
      if (!t || !m) continue;
      byTicker.set(t, { dates: m.dates || [], values: m.values || [] });
    }

    // Union of dates, parsed similar to sector funding heatmap
    const parseDateToKey = (d: string | number): number => {
      if (typeof d === 'number') {
        if (d >= 10000101 && d <= 99991231) {
          const y = Math.floor(d / 10000);
          const m = Math.floor((d % 10000) / 100);
          const day = d % 100;
          return Date.UTC(y, m - 1, day);
        }
        // seconds vs ms
        return d < 1e12 ? d * 1000 : d;
      }
      const p = Date.parse(d);
      return Number.isFinite(p) ? p : 0;
    };

    const dateSet = new Set<number>();
    for (const t of tickers) {
      const s = byTicker.get(t);
      for (const dd of (s?.dates || [])) dateSet.add(parseDateToKey(dd));
    }
    const allDates = Array.from(dateSet).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
    const points = RANGE_TO_POINTS[range] ?? 30;
    const datesSlice = allDates.slice(-points);
    const dateLabels = datesSlice.map((ms) => {
      const d = new Date(ms);
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(d.getUTCDate()).padStart(2, '0');
      return `${mm}-${dd}`;
    });
    const dateToIndex = new Map<number, number>();
    for (let i = 0; i < allDates.length; i++) dateToIndex.set(allDates[i]!, i);
    const sliceIndices: number[] = datesSlice.map((d) => dateToIndex.get(d) ?? -1);

    const isFundingRate = /futures_fr/i.test(String(xKey));
    // Build aligned values per token based on selected transform
    const data: Array<[number, number, number]> = [];
    for (let row = 0; row < tickers.length; row++) {
      const t = tickers[row];
      if (!t) continue;
      const s = byTicker.get(t);
      if (!s) continue;
      const map = new Map<number, number>();
      for (let i = 0; i < s.dates.length; i++) {
        const k = parseDateToKey(s.dates[i]!);
        const v = Number(s.values[i] ?? null);
        if (Number.isFinite(k) && Number.isFinite(v)) map.set(k, v);
      }
      const alignedFull: number[] = new Array(allDates.length).fill(NaN);
      for (let i = 0; i < allDates.length; i++) {
        const v = map.get(allDates[i]!);
        alignedFull[i] = (typeof v === 'number' && Number.isFinite(v)) ? v : NaN;
      }
      const alignedSlice: number[] = sliceIndices.map((idx) => (idx >= 0 ? alignedFull[idx]! : NaN));

      if (isFundingRate && transform === 'raw') {
        // Convert funding rate to APR similar to sector heatmap (8h rate * 3 * 365)
        for (let col = 0; col < datesSlice.length; col++) {
          const v = alignedSlice[col];
          if (!Number.isFinite(v)) continue;
          data.push([col, row, (v as number) * 1095]);
        }
        continue;
      }

      // Derived transforms for non-funding metrics (and funding if not raw)
      const computeZScores = (arr: number[]) => {
        const valid = arr.filter((v) => Number.isFinite(v)) as number[];
        const n = valid.length;
        if (n === 0) return [] as number[];
        const mean = valid.reduce((s, v) => s + v, 0) / n;
        const variance = valid.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / n;
        const std = Math.sqrt(variance) || 1;
        return arr.map((v) => (Number.isFinite(v) ? (v - mean) / std : NaN));
      };

      const computeMomentum = (arr: number[], window: number) => {
        // Momentum as rolling mean of daily returns over N days
        const n = arr.length;
        const daily: number[] = new Array(n).fill(NaN);
        for (let i = 1; i < n; i++) {
          const a = arr[i];
          const b = arr[i - 1];
          if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(b as number) > 0) {
            daily[i] = ((a as number) - (b as number)) / Math.abs(b as number);
          }
        }
        const out: number[] = new Array(n).fill(NaN);
        let sum = 0;
        const q: number[] = [];
        for (let i = 0; i < n; i++) {
          const v = daily[i];
          if (Number.isFinite(v)) {
            q.push(v as number);
            sum += v as number;
          }
          if (q.length > window) {
            const removed = q.shift() as number | undefined;
            if (removed !== undefined) sum -= removed;
          }
          if (q.length >= Math.min(2, window)) {
            out[i] = sum / q.length;
          }
        }
        return out;
      };

      const computeRollingVol = (arr: number[], window: number) => {
        // Volatility on daily returns, rolling window
        const n = arr.length;
        const daily: number[] = new Array(n).fill(NaN);
        for (let i = 1; i < n; i++) {
          const a = arr[i];
          const b = arr[i - 1];
          if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(b as number) > 0) {
            daily[i] = ((a as number) - (b as number)) / Math.abs(b as number);
          }
        }
        const out: number[] = new Array(n).fill(NaN);
        for (let i = 0; i < n; i++) {
          const start = i - window + 1;
          if (start < 0) continue;
          const slice = daily.slice(start, i + 1).filter((v) => Number.isFinite(v)) as number[];
          if (slice.length < 2) continue;
          const mean = slice.reduce((s, v) => s + v, 0) / slice.length;
          const variance = slice.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / slice.length;
          out[i] = Math.sqrt(variance);
        }
        return out;
      };

      // dominance handled after building matrix

      let transformedSlice: number[];
      if (transform === 'momentum') {
        const full = computeMomentum(alignedFull, windowDays === 7 ? 7 : windowDays === 30 ? 30 : 90);
        transformedSlice = sliceIndices.map((idx) => (idx >= 0 ? full[idx]! : NaN));
      } else if (transform === 'vol') {
        const full = computeRollingVol(alignedFull, windowDays === 7 ? 7 : windowDays === 30 ? 30 : 90);
        transformedSlice = sliceIndices.map((idx) => (idx >= 0 ? full[idx]! : NaN));
      } else if (transform === 'dominance') {
        transformedSlice = alignedSlice.slice();
      } else {
        transformedSlice = alignedSlice.slice();
      }

      // For raw (non-FR) and momentum/vol, use z-score to enhance comparability across tokens
      if (transform !== 'dominance') {
        const z = computeZScores(transformedSlice);
        for (let col = 0; col < z.length; col++) {
          const v = z[col];
          if (!Number.isFinite(v)) continue;
          data.push([col, row, v as number]);
        }
      } else {
        // Temporarily store as NaN; will compute cross-sectional mean below
        for (let col = 0; col < transformedSlice.length; col++) {
          const v = transformedSlice[col];
          if (!Number.isFinite(v)) continue;
          data.push([col, row, v as number]);
        }
      }
    }

    // If dominance selected, recompute values: divide by cross-sectional mean per column, then z-score across periods per token
    if (transform === 'dominance' && data.length) {
      // Build matrix shape [rows x cols]
      const cols = datesSlice.length;
      const rowsCount = tickers.length;
      const matrix: number[][] = Array.from({ length: rowsCount }, () => Array(cols).fill(NaN));
      for (const [c, r, v] of data) {
        if (r >= 0 && r < rowsCount && c >= 0 && c < cols) {
          matrix[r]![c] = v as number;
        }
      }
      // Cross-sectional mean per column
      const csMean: number[] = Array(cols).fill(NaN);
      for (let c = 0; c < cols; c++) {
        const colVals = matrix.map((row) => row[c]).filter((v) => Number.isFinite(v)) as number[];
        csMean[c] = colVals.length ? colVals.reduce((s, v) => s + v, 0) / colVals.length : NaN;
      }
      // Normalize and then z-score per row
      const out: Array<[number, number, number]> = [];
      for (let r = 0; r < rowsCount; r++) {
        const rowArr = matrix[r] || [];
        const norm = rowArr.map((v, idx) => (Number.isFinite(v) && Number.isFinite(csMean[idx] as number) && (csMean[idx] as number) !== 0) ? (v as number) / (csMean[idx] as number) : NaN);
        const valid = norm.filter((v) => Number.isFinite(v)) as number[];
        if (valid.length === 0) continue;
        const mean = valid.reduce((s, v) => s + v, 0) / valid.length;
        const variance = valid.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / valid.length;
        const std = Math.sqrt(variance) || 1;
        for (let c = 0; c < cols; c++) {
          const v = norm[c];
          if (!Number.isFinite(v)) continue;
          out.push([c, r, ((v as number) - mean) / std]);
        }
      }
      // Replace data with dominance output
      data.length = 0;
      data.push(...out);
    }

    const absMax = data.length ? Math.max(...data.map(([, , v]) => Math.abs(Number(v) || 0))) : 1;
    const cap = isFundingRate ? Math.max(10, Math.min(200, absMax * 1.2)) : Math.max(1, absMax * 1.1);

    // Filter out rows (tokens) with no data and reindex rows for yAxis
    const rowHasData: boolean[] = Array.from({ length: tickers.length }, () => false);
    for (const [, rowIdx] of data.map((d) => [d[0], d[1]])) {
      rowHasData[rowIdx as number] = true;
    }
    const oldToNewIndex = new Map<number, number>();
    const filteredTickers: string[] = [];
    for (let i = 0, j = 0; i < tickers.length; i++) {
      if (rowHasData[i]) { oldToNewIndex.set(i, j); filteredTickers.push(tickers[i] ?? ''); j++; }
    }
    const reindexedData: Array<[number, number, number]> = [];
    for (const [col, row, val] of data) {
      const ni = oldToNewIndex.get(row);
      if (ni == null) continue;
      reindexedData.push([col, ni, val]);
    }

    // If clustered, reorder rows using hierarchical clustering (average linkage with correlation distance)
    let finalTickers = filteredTickers;
    let finalData = reindexedData as Array<[number, number, number]>;
    if (clustered && filteredTickers.length >= 3) {
      const rowsCount = filteredTickers.length;
      const cols = datesSlice.length;
      // Build dense matrix [rows x cols]
      const matrix: number[][] = Array.from({ length: rowsCount }, () => Array(cols).fill(NaN));
      for (const [c, r, v] of reindexedData) {
        if (r >= 0 && r < rowsCount && c >= 0 && c < cols) matrix[r]![c] = Number(v);
      }
      const pearsonCorr = (a: number[], b: number[]): number => {
        let n = 0;
        let sumA = 0, sumB = 0, sumAA = 0, sumBB = 0, sumAB = 0;
        for (let i = 0; i < a.length && i < b.length; i++) {
          const va = a[i];
          const vb = b[i];
          if (Number.isFinite(va) && Number.isFinite(vb)) {
            n++;
            sumA += va as number;
            sumB += vb as number;
            sumAA += (va as number) * (va as number);
            sumBB += (vb as number) * (vb as number);
            sumAB += (va as number) * (vb as number);
          }
        }
        if (n < 2) return 0; // insufficient overlap
        const cov = sumAB - (sumA * sumB) / n;
        const varA = sumAA - (sumA * sumA) / n;
        const varB = sumBB - (sumB * sumB) / n;
        const denom = Math.sqrt(Math.max(varA, 0)) * Math.sqrt(Math.max(varB, 0));
        if (denom === 0) return 0;
        const corr = cov / denom;
        return Math.max(-1, Math.min(1, corr));
      };
      // Precompute pairwise leaf distances (1 - corr)
      const leafDist: number[][] = Array.from({ length: rowsCount }, () => Array(rowsCount).fill(1));
      for (let i = 0; i < rowsCount; i++) {
        for (let j = i + 1; j < rowsCount; j++) {
          const d = 1 - pearsonCorr(matrix[i]!, matrix[j]!);
          leafDist[i]![j] = d;
          leafDist[j]![i] = d;
        }
      }
      type Cluster = { members: number[] };
      const clusters: Cluster[] = Array.from({ length: rowsCount }, (_, i) => ({ members: [i] }));
      const avgLinkage = (a: Cluster, b: Cluster): number => {
        let sum = 0; let cnt = 0;
        for (const i of a.members) {
          for (const j of b.members) {
            sum += leafDist[i]![j]!;
            cnt++;
          }
        }
        return cnt === 0 ? 1 : (sum / cnt);
      };
      while (clusters.length > 1) {
        let bestI = 0, bestJ = 1, bestD = avgLinkage(clusters[0]!, clusters[1]!);
        for (let i = 0; i < clusters.length; i++) {
          for (let j = i + 1; j < clusters.length; j++) {
            const d = avgLinkage(clusters[i]!, clusters[j]!);
            if (d < bestD) { bestD = d; bestI = i; bestJ = j; }
          }
        }
        const left = clusters[bestI]!.members;
        const right = clusters[bestJ]!.members;
        // Simple orientation heuristic: choose concat order that minimizes edge distance between boundary elements
        const leftFirst = leafDist[left[left.length - 1]!]![right[0]!]!;
        const rightFirst = leafDist[right[right.length - 1]!]![left[0]!]!;
        const merged = (leftFirst <= rightFirst) ? [...left, ...right] : [...right, ...left];
        const newCluster: Cluster = { members: merged };
        // Remove j then i to keep indices valid
        clusters.splice(bestJ, 1);
        clusters.splice(bestI, 1);
        clusters.push(newCluster);
      }
      const order = clusters[0]?.members ?? [];
      if (order.length === rowsCount) {
        const oldToNew = new Map<number, number>();
        for (let i = 0; i < order.length; i++) oldToNew.set(order[i]!, i);
        const clusteredData: Array<[number, number, number]> = [];
        for (const [c, r, v] of reindexedData) {
          const nr = oldToNew.get(r);
          if (nr == null) continue;
          clusteredData.push([c, nr, v]);
        }
        finalData = clusteredData;
        finalTickers = order.map((oldIdx) => filteredTickers[oldIdx] ?? '');
      }
    }

    return {
      backgroundColor: 'transparent',
      tooltip: {
        position: 'top',
        formatter: (p: unknown) => {
          const pr = p as { value: [number, number, number] };
          const [x, y, v] = pr.value;
          const label = `${finalTickers[y] ?? ''} — ${dateLabels[x] ?? ''}`;
          if (isFundingRate) {
            return `${label}<br/>${labelFromCatalog(String(xKey))} (APR): ${Number(v).toFixed(1)}%`;
          }
      let labelSuffix = 'z';
      if (transform === 'momentum') labelSuffix = 'mom z';
      else if (transform === 'vol') labelSuffix = 'vol z';
      else if (transform === 'dominance') labelSuffix = 'dom z';
      return `${label}<br/>${labelFromCatalog(String(xKey))} ${labelSuffix}: ${Number(v).toFixed(2)}`;
        }
      },
      grid: { left: 70, right: 16, top: 28, bottom: 60 },
      xAxis: { type: 'category', data: dateLabels, axisLabel: { color: '#94a3b8' }, axisTick: { show: false }, axisLine: { show: false } },
      yAxis: { type: 'category', data: finalTickers, axisLabel: { color: '#94a3b8', width: 84, overflow: 'truncate' } },
      visualMap: {
        min: -cap,
        max: cap,
        show: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        inRange: { color: ['#4b006e', '#2b2f77', '#1e6f8f', '#14a38b', '#5fd067', '#ffd12a', '#ff8a00'] }
      },
      series: [ { type: 'heatmap', data: finalData, label: { show: false }, itemStyle: { borderWidth: 0 } } ],
    } as EChartsOption;
  }, [historyQuery.data, tickers, range, xKey, labelFromCatalog, transform, windowDays, clustered]);

  const hasData = useMemo(() => {
    const historyData = historyQuery.data || [];
    return historyData.some((tokenData) => {
      const vals = tokenData.metrics?.[0]?.values;
      return Array.isArray(vals) && vals.length > 0;
    });
  }, [historyQuery.data]);

  const isLoading = historyQuery.isLoading;
  const error = historyQuery.error as Error | null | undefined;

  return (
    <EChartsRenderer chartOption={option} height={640} isLoading={isLoading} error={error?.message || null} hasData={hasData} containerClassName="bg-transparent border-0 rounded-lg overflow-hidden" />
  );
};

export default HeatmapChart;


